import { Component, type ReactNode } from 'react'

interface State {
  error: Error | null
}

interface Props {
  children: ReactNode
  fallbackTitle?: string
}

/** Catches render-time exceptions so a thrown error in one component
 *  doesn't blank out the whole route. Renders a small readable error
 *  card and a Reload button instead.  */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error('ErrorBoundary caught:', error, info)
  }

  render() {
    if (this.state.error) {
      const msg = this.state.error.message || String(this.state.error)
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-800 mb-2">
            {this.props.fallbackTitle || 'Something went wrong rendering this view'}
          </h2>
          <p className="text-sm text-red-700 mb-3">
            The data was loaded but the page hit an unexpected error while drawing it.
            This often happens with very large analyses.
          </p>
          <pre className="text-xs bg-white p-3 rounded border border-red-100 overflow-x-auto mb-3">
            {msg}
          </pre>
          <div className="flex gap-2">
            <button
              onClick={() => this.setState({ error: null })}
              className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700"
            >
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded hover:bg-red-100"
            >
              Reload page
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
